// slices/auth/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { posthog } from 'posthog-js';

// Define status types
type AuthStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AuthState {
  authToken: string | null;
  refreshToken: string | null;
  userId: string | null; // Store the user ID
  isAuthenticated: boolean; // Explicit flag
  status: AuthStatus; // Track auth status
  error: string | null; // Store potential auth errors
}

// Helper to check initial auth state from localStorage
const getInitialAuthState = (): AuthState => {
  const authToken = localStorage.getItem('authToken');
  const refreshToken = localStorage.getItem('refreshToken');
  // You might store userId too, or decode it from the token if needed initially
  // For simplicity, we'll set userId on login/refresh only
  const userId = localStorage.getItem('userId'); // Optional: store userId too

  return {
    authToken,
    refreshToken,
    userId: userId || null,
    isAuthenticated: !!authToken, // Simple check based on token presence
    status: 'idle',
    error: null,
  };
};

const initialState: AuthState = getInitialAuthState();

export const authSlice = createSlice({
  name: 'auth', // Changed name to 'auth' for clarity
  initialState,
  reducers: {
    // Action dispatched when login/refresh starts
    authLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    // Action dispatched on successful login/refresh
    authSuccess: (
      state,
      action: PayloadAction<{
        authToken: string;
        refreshToken: string;
        userId: string; // Require userId on success
        isAuthenticated?: boolean;
      }>
    ) => {
      const { authToken, refreshToken, userId, isAuthenticated } =
        action.payload;
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userId', userId); // Store userId
      state.authToken = authToken;
      state.refreshToken = refreshToken;
      state.userId = userId;
      state.isAuthenticated = isAuthenticated ?? true;
      state.status = 'succeeded';
      state.error = null;
    },
    // Action dispatched on auth failure (login, refresh, or invalid token)
    authFailed: (state, action: PayloadAction<string | null>) => {
      // Clear sensitive data on failure
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('persist:root'); // Keep this if you use persist elsewhere
      state.authToken = null;
      state.refreshToken = null;
      state.userId = null;
      state.isAuthenticated = false;
      state.status = 'failed';
      state.error = action.payload ?? 'Authentication failed';
    },
    // Action for explicit logout
    logout: (state) => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('persist:root'); // Keep this
      posthog?.reset(); // Resets distinct_id for the user
      state.authToken = null;
      state.refreshToken = null;
      state.userId = null;
      state.isAuthenticated = false;
      state.status = 'idle'; // Reset status
      state.error = null;
      // NOTE: Clearing userInfo/exams etc. will happen in their respective slices
    },
    // Optional: If you only update tokens without full auth flow (e.g., impersonation)
    setTokens: (
      state,
      action: PayloadAction<{
        authToken: string;
        refreshToken: string;
        userId: string;
        isAuthenticated?: boolean;
      }>
    ) => {
      const { authToken, refreshToken, userId, isAuthenticated } =
        action.payload;
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userId', userId);
      state.authToken = authToken;
      state.refreshToken = refreshToken;
      state.userId = userId;
      state.isAuthenticated = isAuthenticated ?? true;
      state.status = 'succeeded';
      state.error = null;
    },
  },
});

export const { authLoading, authSuccess, authFailed, logout, setTokens } =
  authSlice.actions;

export default authSlice.reducer;
